import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import type { Project } from '@hangar/shared';
import { Icon } from '@/components/Icon';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { NewProjectDialog } from '@/features/projects/NewProjectForm';

/**
 * Shown once every setup step is done: the way to start the next thing. "New project" opens the project dialog (the
 * new project is then selected); "New app" opens the selected project's page with Connect app open (`?connect=1`).
 */
export function StartSomethingNew({ project, onProject }: { project: Project | undefined; onProject: (slug: string) => void }) {
  const [creating, setCreating] = useState(false);
  return (
    <Card data-testid="start-something-new" className="enter min-w-0">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Icon name="add_circle" size={20} className="text-primary" />
          Start something new
        </CardTitle>
        <CardDescription>Setup is complete. Add another project, or another app to {project ? project.name : 'a project'}; each one walks the same steps.</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" data-testid="start-new-project" onClick={() => setCreating(true)} aria-haspopup="dialog">
            <Icon name="account_tree" size={18} />
            New project
          </Button>
          {project ? (
            <Button asChild variant="outline">
              <Link to="/projects/$slug" params={{ slug: project.slug }} search={{ connect: true }} data-testid="start-new-app">
                <Icon name="apps" size={18} />
                New app in {project.name}
              </Link>
            </Button>
          ) : null}
        </div>
        <NewProjectDialog open={creating} onOpenChange={setCreating} onCreated={(created) => onProject(created.slug)} />
      </CardContent>
    </Card>
  );
}
